import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup } from "react-simple-maps";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const NAME_ALIASES = {
    "united states of america": "usa",
    "united states": "usa",
    "usa": "usa",
    "us": "usa",
    "russia": "russia",
    "russian federation": "russia",
    "south korea": "south korea",
    "republic of korea": "south korea",
    "north korea": "north korea",
    "dem. rep. korea": "north korea",
    "czechia": "czech republic",
    "czech rep.": "czech republic",
    "dominican rep.": "dominican republic",
    "uk": "united kingdom",
    "great britain": "united kingdom",
    "britain": "united kingdom",
    "uae": "united arab emirates",
};

const norm = (s) => {
    if (!s) return "";
    const k = String(s).toLowerCase().trim();
    return NAME_ALIASES[k] || k;
};

// Approximate country centroids so map modes work even when the stamp lat/lng is missing.
// Populous / common travel countries only — extend as needed.
const COUNTRY_CENTROID = {
    "france": [2.35, 46.6], "italy": [12.5, 42.5], "spain": [-3.7, 40.2], "portugal": [-8.4, 39.6],
    "germany": [10.5, 51.2], "united kingdom": [-1.5, 54.0], "ireland": [-8.0, 53.4],
    "greece": [22.0, 39.0], "netherlands": [5.5, 52.2], "belgium": [4.5, 50.6], "switzerland": [8.2, 46.8],
    "austria": [14.5, 47.6], "sweden": [15.0, 62.0], "norway": [10.0, 62.0], "denmark": [9.5, 56.0],
    "finland": [26.0, 64.5], "poland": [19.5, 52.0], "czech republic": [15.5, 49.8],
    "croatia": [16.0, 45.2], "iceland": [-19.0, 64.9], "turkey": [35.0, 39.0], "russia": [90.0, 60.0],
    "usa": [-98.5, 39.0], "canada": [-96.0, 56.0], "mexico": [-102.5, 23.5],
    "cuba": [-79.5, 21.5], "puerto rico": [-66.5, 18.2], "dominican republic": [-70.5, 19.0],
    "colombia": [-73.0, 4.0], "brazil": [-53.0, -10.0], "argentina": [-64.0, -34.0], "chile": [-71.5, -35.7],
    "peru": [-75.0, -10.0], "ecuador": [-78.0, -1.5], "costa rica": [-84.0, 9.9], "panama": [-80.0, 9.0],
    "japan": [138.0, 37.0], "china": [104.0, 35.0], "south korea": [128.0, 36.5], "north korea": [127.0, 40.0],
    "thailand": [101.0, 15.0], "vietnam": [107.5, 16.0], "indonesia": [117.0, -2.5], "philippines": [122.0, 13.0],
    "malaysia": [102.5, 4.0], "singapore": [103.8, 1.35], "india": [78.0, 22.0], "sri lanka": [80.7, 7.9],
    "nepal": [84.0, 28.4], "bhutan": [90.5, 27.5], "cambodia": [104.9, 12.5], "laos": [102.5, 18.0],
    "australia": [134.0, -25.0], "new zealand": [172.0, -41.0], "fiji": [178.0, -18.0],
    "egypt": [30.0, 27.0], "morocco": [-7.0, 32.0], "tunisia": [9.5, 34.0], "south africa": [24.0, -29.0],
    "kenya": [37.9, 0.0], "tanzania": [34.9, -6.4], "ethiopia": [39.0, 9.0], "namibia": [17.0, -22.0],
    "botswana": [24.7, -22.3], "zimbabwe": [30.0, -19.0], "ghana": [-1.2, 7.9], "senegal": [-14.5, 14.5],
    "united arab emirates": [54.0, 24.0], "saudi arabia": [45.0, 24.0], "qatar": [51.2, 25.3],
    "israel": [35.0, 31.5], "jordan": [36.6, 31.2], "lebanon": [35.8, 33.9], "iran": [53.0, 32.0],
    "uzbekistan": [64.5, 41.4], "kazakhstan": [67.0, 48.0], "mongolia": [104.0, 46.0],
    "hungary": [19.4, 47.0], "romania": [25.0, 46.0], "bulgaria": [25.5, 42.7], "ukraine": [31.5, 49.0],
    "serbia": [21.0, 44.0], "slovenia": [14.9, 46.1], "slovakia": [19.7, 48.7], "estonia": [26.0, 58.7],
    "latvia": [24.7, 57.0], "lithuania": [23.9, 55.3], "malta": [14.5, 35.9], "cyprus": [33.2, 35.1],
    "montenegro": [19.3, 42.8], "bosnia": [17.7, 44.0], "albania": [20.1, 41.1], "north macedonia": [21.7, 41.6],
};

const centroidFor = (country) => COUNTRY_CENTROID[norm(country)] || null;

const MODES = {
    default: { showPins: true, showRoutes: false, showHeat: false, scratchOverlay: false, showLive: false },
    heatmap: { showPins: false, showRoutes: false, showHeat: true, scratchOverlay: false, showLive: false },
    routes: { showPins: true, showRoutes: true, showHeat: false, scratchOverlay: false, showLive: false },
    live: { showPins: true, showRoutes: false, showHeat: false, scratchOverlay: false, showLive: true },
    scratch: { showPins: false, showRoutes: false, showHeat: false, scratchOverlay: true, showLive: false },
};

const REGION_COORDS = {
    "World": { center: [0, 20], zoom: 1 },
    "Europe": { center: [15, 50], zoom: 4 },
    "Asia": { center: [90, 30], zoom: 2.5 },
    "Africa": { center: [20, 0], zoom: 2.5 },
    "N. America": { center: [-100, 45], zoom: 2.5 },
    "S. America": { center: [-60, -20], zoom: 2.5 },
    "Oceania": { center: [140, -25], zoom: 3 },
};

export default function WorldMap({ stamps = [], onCountryClick, mode = "default", region = "World" }) {
    const { t } = useTranslation();
    const [hovered, setHovered] = useState(null);
    const [scratchedSet, setScratchedSet] = useState(new Set());
    const cfg = MODES[mode] || MODES.default;
    const visitedSet = useMemo(() => new Set(stamps.map((s) => norm(s.country))), [stamps]);

    // Reset scratch state when leaving scratch mode
    useEffect(() => {
        if (mode !== "scratch") setScratchedSet(new Set());
    }, [mode]);

    // Density per country — pick the most-stamped for heat weighting
    const countStamps = useMemo(() => {
        const m = new Map();
        for (const s of stamps) {
            const k = norm(s.country);
            m.set(k, (m.get(k) || 0) + 1);
        }
        return m;
    }, [stamps]);
    const maxCount = useMemo(() => Math.max(1, ...Array.from(countStamps.values())), [countStamps]);

    // Chronological path (Routes mode) — order stamps by created_at, keep those with coords
    const pathPoints = useMemo(() => {
        const seq = [...stamps]
            .filter((s) => s.lat != null && s.lng != null && !isNaN(Number(s.lat)) && !isNaN(Number(s.lng)))
            .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        return seq.map((s) => ({ coords: [Number(s.lng), Number(s.lat)], country: s.country, id: s.id }));
    }, [stamps]);

    const activeModeLabel = t(`worldMap.modes.${mode}`, { defaultValue: mode });

    return (
        <div className="relative rounded-3xl overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="world-map">
            {/* Overlay stats */}
            <div className="absolute top-6 left-6 z-10 pointer-events-none">
                <div className="overline mb-2">{t("worldMap.overline", { mode: activeModeLabel })}</div>
                <div className="font-serif text-3xl md:text-4xl text-white leading-none">
                    {visitedSet.size} <span className="text-white/50 text-xl">/ 195</span>
                </div>
                <div className="text-white/50 text-xs mt-1">{t("worldMap.visitedSub")}</div>
            </div>

            {/* Right-side mode legend */}
            <div className="absolute top-6 right-6 z-10 flex items-center gap-4 text-[10px] uppercase tracking-[0.15em] text-white/60">
                {mode === "heatmap" && (
                    <>
                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "rgba(244,162,143,0.7)" }} /> {t("worldMap.legend.hot")}</span>
                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "rgba(212,175,55,0.35)" }} /> {t("worldMap.legend.warm")}</span>
                    </>
                )}
                {mode === "routes" && (
                    <span className="flex items-center gap-2"><span className="w-6 h-[2px] inline-block" style={{ background: "var(--gold)" }} /> {t("worldMap.legend.chronological")}</span>
                )}
                {mode === "live" && (
                    <span className="flex items-center gap-2"><LivePulse small /> {t("worldMap.legend.pings", { count: stamps.length })}</span>
                )}
                {mode === "scratch" && (
                    <span className="flex items-center gap-2 text-white/70">{t("worldMap.legend.scratch")}</span>
                )}
                {mode === "default" && (
                    <>
                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "var(--gold)" }} /> {t("worldMap.legend.visited")}</span>
                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "rgba(255,255,255,0.08)" }} /> {t("worldMap.legend.toExplore")}</span>
                    </>
                )}
            </div>

            <ComposableMap
                projection="geoEqualEarth"
                projectionConfig={{ scale: 155 }}
                style={{ width: "100%", height: "auto", cursor: mode === "scratch" ? "crosshair" : "default" }}
            >
                <ZoomableGroup 
                    center={REGION_COORDS[region]?.center || [0, 20]} 
                    zoom={REGION_COORDS[region]?.zoom || 1}
                    filterZoomEvent={(e) => e.type !== "wheel"}
                >
                {/* Radial gradient for heatmap markers */}
                <defs>
                    <radialGradient id="heat-hot" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(244,162,143,0.85)" />
                        <stop offset="60%" stopColor="rgba(244,162,143,0.25)" />
                        <stop offset="100%" stopColor="rgba(244,162,143,0)" />
                    </radialGradient>
                    <radialGradient id="heat-warm" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(212,175,55,0.75)" />
                        <stop offset="70%" stopColor="rgba(212,175,55,0.2)" />
                        <stop offset="100%" stopColor="rgba(212,175,55,0)" />
                    </radialGradient>
                    <radialGradient id="live-pulse" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(159,213,208,0.9)" />
                        <stop offset="70%" stopColor="rgba(159,213,208,0.2)" />
                        <stop offset="100%" stopColor="rgba(159,213,208,0)" />
                    </radialGradient>
                </defs>

                <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                        geographies.map((geo) => {
                            const name = geo.properties.name;
                            const key = norm(name);
                            const isVisited = visitedSet.has(key);
                            const isScratched = mode === "scratch" && scratchedSet.has(key);
                            const heatCount = countStamps.get(key) || 0;
                            const heatRatio = heatCount / maxCount; // 0..1

                            let fill;
                            let opacity = 1;

                            if (mode === "heatmap") {
                                fill = heatCount > 0
                                    ? `rgba(212,175,55, ${0.12 + heatRatio * 0.28})`
                                    : "rgba(255,255,255,0.04)";
                            } else if (mode === "scratch") {
                                fill = (isVisited || isScratched) ? "#D4AF37" : "rgba(120,120,130,0.18)";
                                opacity = (isVisited || isScratched) ? 1 : 0.85;
                            } else if (mode === "routes") {
                                fill = isVisited ? "rgba(212,175,55,0.85)" : "rgba(255,255,255,0.045)";
                            } else if (mode === "live") {
                                fill = isVisited ? "#D4AF37" : "rgba(255,255,255,0.04)";
                            } else {
                                fill = isVisited ? "#D4AF37" : "rgba(255,255,255,0.055)";
                                opacity = isVisited ? 0.9 : 1;
                            }

                            return (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    onMouseEnter={() => {
                                        setHovered(name);
                                        if (mode === "scratch" && !isVisited) {
                                            setScratchedSet((prev) => new Set([...prev, key]));
                                        }
                                    }}
                                    onMouseLeave={() => setHovered(null)}
                                    onClick={() => onCountryClick?.(name, isVisited)}
                                    style={{
                                        default: {
                                            fill,
                                            stroke: mode === "scratch" ? "rgba(180,180,180,0.25)" : "rgba(11,19,32,0.9)",
                                            strokeWidth: 0.5,
                                            outline: "none",
                                            transition: mode === "scratch" ? "fill 100ms ease" : "fill 400ms ease, opacity 400ms ease",
                                            opacity,
                                        },
                                        hover: {
                                            fill: mode === "scratch" && !isVisited
                                                ? "#D4AF37"
                                                : (isVisited ? "#E8DCC4" : "rgba(212,175,55,0.3)"),
                                            outline: "none",
                                            cursor: mode === "scratch" ? "crosshair" : "pointer",
                                        },
                                        pressed: { fill: "#D4AF37", outline: "none" },
                                    }}
                                    data-testid={`map-country-${name.toLowerCase().replace(/\s/g, "-")}`}
                                />
                            );
                        })
                    }
                </Geographies>

                {/* Routes: chronological polyline connecting stamps */}
                {cfg.showRoutes && pathPoints.length > 1 && pathPoints.slice(0, -1).map((p, i) => {
                    const next = pathPoints[i + 1];
                    return (
                        <Line
                            key={`route-${i}`}
                            from={p.coords}
                            to={next.coords}
                            stroke="var(--gold)"
                            strokeWidth={1.6}
                            strokeLinecap="round"
                            strokeDasharray="1,4"
                        />
                    );
                })}

                {/* Heatmap density blobs */}
                {cfg.showHeat && stamps.filter((s) => s.lat != null && s.lng != null).map((s, i) => {
                    const count = countStamps.get(norm(s.country)) || 1;
                    const ratio = count / maxCount;
                    const zoomVal = REGION_COORDS[region]?.zoom || 1;
                    const radius = (4 + ratio * 9) / Math.max(1, zoomVal * 0.65);
                    const grad = ratio > 0.6 ? "heat-hot" : "heat-warm";
                    return (
                        <Marker key={`heat-${s.id || i}`} coordinates={[Number(s.lng), Number(s.lat)]}>
                            <circle r={radius} fill={`url(#${grad})`} style={{ pointerEvents: "none" }} />
                        </Marker>
                    );
                })}

                {/* Pins */}
                {cfg.showPins && pathPoints.map((p, idx) => (
                    <Marker key={p.id || `pin-${idx}`} coordinates={p.coords}>
                        <motion.circle
                            initial={{ r: 0, opacity: 0 }}
                            animate={{ r: cfg.showRoutes ? 3.2 : 3, opacity: 1 }}
                            transition={{ duration: 0.6, delay: cfg.showRoutes ? idx * 0.08 : 0 }}
                            fill="#0B1320"
                            stroke="#D4AF37"
                            strokeWidth={1.4}
                        />
                        {cfg.showRoutes && (
                            <text
                                x={5}
                                y={2}
                                fontSize={7}
                                fill="rgba(255,255,255,0.55)"
                                style={{ fontFamily: "monospace", pointerEvents: "none" }}
                            >{idx + 1}</text>
                        )}
                    </Marker>
                ))}

                {/* Live Pings Mode */}
                <AnimatePresence>
                    {cfg.showLive && stamps.slice(-12).map((s, i) => (
                        s.lng && s.lat && (
                            <Marker key={`live-${s.id || i}`} coordinates={[Number(s.lng), Number(s.lat)]}>
                                <motion.circle r={10} fill="url(#live-pulse)" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: [0, 1, 0.2], scale: [0.5, 2, 1] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }} style={{ pointerEvents: "none" }} />
                                <circle r={2} fill="#9FD5D0" style={{ pointerEvents: "none" }} />
                            </Marker>
                        )
                    ))}
                </AnimatePresence>
                </ZoomableGroup>
            </ComposableMap>

            {/* Scratch mode: soft grain overlay to feel like a scratch card */}
            {cfg.scratchOverlay && (
                <div
                    className="absolute inset-0 pointer-events-none mix-blend-overlay"
                    style={{
                        background: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.06), transparent 40%), radial-gradient(circle at 70% 70%, rgba(159,213,208,0.05), transparent 40%)",
                    }}
                />
            )}

            {/* Live mode caption */}
            {cfg.showLive && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-6 right-6 z-10 rounded-full glass px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white/80 flex items-center gap-2"
                    data-testid="map-live-caption"
                >
                    <LivePulse /> {t("worldMap.captions.explorersMoving", { count: Math.max(2, Math.min(stamps.length, 12)) })}
                </motion.div>
            )}

            {/* Routes caption */}
            {cfg.showRoutes && pathPoints.length >= 2 && (
                <div className="absolute bottom-6 right-6 z-10 rounded-full glass px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white/80" data-testid="map-routes-caption">
                    {t("worldMap.captions.routesSummary", { stops: pathPoints.length, distance: (distanceKm(pathPoints)).toLocaleString() })}
                </div>
            )}

            {/* Interactive Tooltip matching original style */}
            <AnimatePresence>
                {hovered && (
                    <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
                        className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-2xl pointer-events-none flex items-center gap-3 glass"
                        style={{ border: "1px solid rgba(255,255,255,0.15)" }}
                    >
                        <span className="font-serif text-2xl text-white whitespace-nowrap">{hovered}</span>
                        {visitedSet.has(norm(hovered)) ? (
                            <span className="flex items-center gap-1 text-[11px] uppercase tracking-widest" style={{ color: "var(--gold)" }}>
                                <Trophy size={14} weight="fill" /> {t("worldMap.tooltip.explored")}
                            </span>
                        ) : (
                            <span className="text-white/40 text-[10px] uppercase tracking-widest whitespace-nowrap">{t("worldMap.tooltip.uncharted")}</span>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function LivePulse({ small }) {
    return (
        <span className="relative inline-block" style={{ width: small ? 6 : 8, height: small ? 6 : 8 }}>
            <motion.span
                className="absolute inset-0 rounded-full"
                style={{ background: "#9FD5D0" }}
                animate={{ scale: [1, 2.4, 1], opacity: [0.9, 0, 0.9] }}
                transition={{ duration: 1.8, repeat: Infinity }}
            />
            <span className="absolute inset-0 rounded-full" style={{ background: "#9FD5D0" }} />
        </span>
    );
}

// Approximate haversine sum in km along ordered pathPoints
function distanceKm(points) {
    if (points.length < 2) return 0;
    let d = 0;
    for (let i = 1; i < points.length; i++) {
        d += haversine(points[i - 1].coords, points[i].coords);
    }
    return Math.round(d);
}

function haversine([lon1, lat1], [lon2, lat2]) {
    const R = 6371;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
}
